import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import Toast from '../components/Toast';
import SellShareModal from '../components/SellShareModal';
import VerifyEmailGate from '../components/VerifyEmailGate';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { playTick } from '../lib/tick';
import { isSqrtClan } from '../lib/clan';
import { sqrtBuySubtotal, sqrtSellSubtotal, sqrtPriceAt, computeTradeQuote } from '../lib/curve';
import { formatPts } from '../lib/format';
import ReplayModal from '../components/ReplayModal';

/**
 * 05 — Trading Sheet (Step 4: REAL trades).
 * Buy/Sell posts to /api/trade/{buy|sell}. Balance & price refresh after.
 */
export default function TradingSheet() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  // Honor `?mode=sell` (or `?mode=buy`) from the URL so external entry
  // points (Wallet Holdings row, share links, deep links) can pre-select
  // the trader's intent instead of always dumping them into Buy.
  const initialMode = searchParams.get('mode') === 'sell' ? 'sell' : 'buy';
  const [mode, setMode] = useState(initialMode);
  const [qty, setQty] = useState(initialMode === 'sell' ? 1 : 3);
  // One-shot flag — bump qty to a smart sell default AFTER holdings load
  // when we opened directly in Sell mode via URL. Ref (not state) so it
  // doesn't re-trigger.
  const sellQtySeededRef = useRef(false);
  const [balance, setBalance] = useState(null);
  const [holding, setHolding] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [flash, setFlash] = useState(null); // { kind: 'success'|'error', msg }
  const [sellShare, setSellShare] = useState(null); // {qty, proceeds, invested} shown post-sell
  // Phase 1 — live price ticker. Polls `/creators/{handle}` every 3s so the
  // buyer sees the price move in real time before they hit Buy. Only the
  // clan snapshot is refetched (not the full user balance / holding), so the
  // network cost is small and the UI stays quiet.
  const [tickAge, setTickAge] = useState(0); // seconds since last successful tick
  const [tickPulse, setTickPulse] = useState(0); // bump to trigger price-flash animation
  const [showFairness, setShowFairness] = useState(false); // "🎲 Fair by design" explainer modal
  const [replayModalOpen, setReplayModalOpen] = useState(false); // Phase 5 — in-app viewer modal
  // Phase 2B — Slippage tolerance. `Auto` = no ceiling sent (trust the engine),
  // `+5/10%` = tighter to looser ceiling. Persisted per user across visits.
  // We DON'T offer +2%: with the frontend's integral approximation for
  // qty>32 (rounding drift ~0.5% vs backend's per-Bit sum), a +2% floor
  // can legitimately bounce a solo trader on large sells. Auto is safest.
  const SLIPPAGE_OPTS = [
    { key: 'auto', label: 'Auto',  pct: null },
    { key: 'p5',   label: '+5%',   pct: 0.05 },
    { key: 'p10',  label: '+10%',  pct: 0.10 },
  ];
  const [slippage, setSlippage] = useState(() => {
    try {
      const raw = localStorage.getItem('clanster.slippage') || 'auto';
      // Migrate legacy `p2` (dropped as unsafe) → auto so nobody is stuck
      // with a tier we no longer render.
      return raw === 'p2' ? 'auto' : raw;
    } catch { return 'auto'; }
  });
  const setSlippagePersist = (k) => {
    setSlippage(k);
    try { localStorage.setItem('clanster.slippage', k); } catch {}
  };
  // Phase 2B — Commit view: while `submitting`, show the "locked · settling"
  // panel with a 0.5s → 0.0s countdown. Value is only cosmetic — the actual
  // batch tick is server-side. When result arrives, `commitInfo` holds the
  // batch_size/batch_position transparency chip data.
  const [commitMs, setCommitMs] = useState(500);
  const [commitInfo, setCommitInfo] = useState(null); // {batch_size, batch_position} or null
  // Live queue-depth poll — populates "N others locked in · est Xs" hint on
  // the Commit strip. Server-authoritative snapshot, 500ms polling cadence.
  const [queueSnap, setQueueSnap] = useState(null);
  useEffect(() => {
    if (!submitting) { setCommitMs(500); setQueueSnap(null); return; }
    setCommitMs(500);
    const start = Date.now();
    const id = setInterval(() => {
      const remaining = Math.max(0, 500 - (Date.now() - start));
      setCommitMs(remaining);
    }, 40);
    // Poll queue depth every 500ms while submitting.
    const handle = creator?.handle;
    let pollId = null;
    if (handle) {
      const poll = async () => {
        try {
          const { data } = await api.get(`/trade/queue-depth/${handle}`);
          setQueueSnap({ queued: data.queued, est_wait_s: data.est_wait_s });
        } catch (_) { /* silent */ }
      };
      poll();
      pollId = setInterval(poll, 500);
    }
    return () => {
      clearInterval(id);
      if (pollId) clearInterval(pollId);
    };
  }, [submitting, creator?.handle]);

  const reload = useCallback(async () => {
    try {
      const [cr, wb, hd] = await Promise.all([
        api.get(`/creators/${handle}`),
        api.get('/wallet/balance'),
        api.get(`/holdings/${handle}`),
      ]);
      setCreator(cr.data);
      setBalance(wb.data.balance);
      const owned = hd.data.bits_owned || 0;
      setHolding(owned);
      // If the URL asked us to open in Sell mode (deep-link from Wallet
      // Holdings, share link, etc.), seed the qty to a sensible sell
      // amount ONCE — matches the qty bump the tab-click handler does.
      // Subsequent qty changes by the user are preserved.
      if (initialMode === 'sell' && !sellQtySeededRef.current) {
        setQty(Math.min(2, Math.max(1, owned)));
        sellQtySeededRef.current = true;
      }
    } catch (e) {
      setError(e.response?.status === 404 ? 'Creator not found.' : 'Could not load.');
    }
  }, [handle]);

  useEffect(() => { reload(); }, [reload]);

  // Phase 1 — Live price ticker. Poll the creator snapshot every 3s so the
  // buyer watches the curve move up in real time. Pauses while submitting so
  // we don't step on the confirm request. Also disabled once we know the clan
  // is not found (creator=null after error) or during pre-launch (nothing to
  // tick).
  useEffect(() => {
    if (!creator || submitting) return undefined;
    const launchAt = creator?.clan?.launch_at;
    if (launchAt && new Date(launchAt) > new Date()) return undefined; // prelaunch
    let cancelled = false;
    let tickCount = 0;
    const id = setInterval(async () => {
      if (cancelled) return;
      tickCount += 1;
      // Bump age counter every second for the "updated Xs ago" indicator.
      setTickAge((n) => n + 1);
      // Refetch the snapshot every 3s (age counter is per-1s for smoothness).
      if (tickCount % 3 !== 0) return;
      try {
        const { data } = await api.get(`/creators/${handle}`);
        if (cancelled) return;
        setCreator((prev) => {
          if (!prev) return data;
          const prevPrice = prev?.clan?.current_price;
          const nextPrice = data?.clan?.current_price;
          if (prevPrice !== nextPrice) setTickPulse((v) => v + 1);
          return data;
        });
        setTickAge(0);
      } catch { /* silent — next tick will retry */ }
    }, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [creator, handle, submitting]);

  const close = () => navigate('/home');

  // ---- Live client-side math (matches server) ----
  const feeRate = 0.11;
  const hasReferrer = !!user?.referred_by;
  const effectiveFeeRate = hasReferrer ? 0.11 : 0.10;

  const { subtotal, fee, avg, total, nextPrice, oversold } = useMemo(() => {
    if (!creator) return { subtotal: 0, fee: 0, avg: 0, total: 0, nextPrice: 0, oversold: false };
    const q = Number(qty) || 0; // '' → 0 during typing
    const { subtotal: s, fee: f, total: t, oversold: over } = computeTradeQuote(
      creator.clan, q, mode, effectiveFeeRate,
    );
    // Next-price after this trade (marginal price of the next Bit).
    const m = creator.clan.multiplier;
    const nMinted = creator.clan.bits_minted;
    const sqrtMode = isSqrtClan(creator.clan);
    const nextN = mode === 'buy' ? nMinted + q + 1 : Math.max(0, nMinted - q) + 1;
    const np = sqrtMode ? sqrtPriceAt(m, nextN) : m * nextN;
    return { subtotal: s, fee: f, avg: q > 0 ? s / q : 0, total: t, nextPrice: np, oversold: over };
  }, [creator, qty, mode, effectiveFeeRate]);

  // Phantom-banner guard: we only surface the "Insufficient balance" warn
  // when the current qty is WITHIN the affordable max. Otherwise the auto-
  // clamp effect (below) will snap qty down on the next render, and showing
  // a scary red banner for a qty the user never chose (e.g., the post-Max-
  // buy reset to 3 when the wallet was just drained) is misleading. The
  // input onChange already clamps to `maxQty` so a user cannot manually
  // type an unaffordable qty — this variable is now defensive-only for the
  // disable logic. Feb 2026 fix.
  const insufficientBalance = mode === 'buy' && balance !== null && total > balance;
  const notEnoughBits = mode === 'sell' && qty > holding;
  const MAX_BUY_QTY = 1_000_000;

  // ---- Fill-scenario projections ("What could happen?") ----
  // Shows the fan what their qty Bits could sell for if the clan grows.
  // Uses the same fill tiers as the Create Clan wizard so creator projections
  // and fan projections speak the same language.
  //
  // Rules:
  //  - BUY view: always show 25% / 60% / 100% so buyer sees full range
  //    (stalled clan = potential loss, growing = potential upside).
  //  - SELL view: only show tiers STRICTLY ABOVE current fill — showing
  //    "what if clan retreats to 25%" to a seller at 60% is nonsense
  //    (they're leaving now, not un-selling). Forward-only makes the
  //    decision framing clear: "if you wait, you could earn more."
  const [showScenarios, setShowScenarios] = useState(false);
  const scenarios = useMemo(() => {
    if (!creator) return null;
    const m = creator.clan.multiplier;
    const supplyCap = creator.clan.supply_cap || 0;
    const nMinted = creator.clan.bits_minted || 0;
    const q = Number(qty) || 0;
    if (q <= 0 || m <= 0) return null;
    const costBasis = total;

    // sqrt clans (no supply cap) — use absolute Bits-minted milestones
    // instead of fill %. Closed-form ∫m√x dx from (target-q) to target ≈
    // (2m/3)(target^1.5 − (target-q)^1.5) — the sell payout for q Bits
    // at that clan size.
    if (isSqrtClan(creator.clan)) {
      const allTiers = [
        { key: 'stall',    label: '1K Bits',   target: 1_000 },
        { key: 'moderate', label: '10K Bits',  target: 10_000 },
        { key: 'full',     label: '100K Bits', target: 100_000 },
      ];
      const activeTiers = mode === 'sell'
        ? allTiers.filter((t) => t.target > nMinted + q)
        : allTiers.filter((t) => t.target >= nMinted);
      if (activeTiers.length === 0) return null;
      return activeTiers.map((t) => {
        const targetMinted = Math.max(t.target, nMinted + q);
        const from = Math.max(0, targetMinted - q);
        // Curve-integral revenue between `from` and `targetMinted` — uses
        // the shared helper so backend + frontend stay in sync on the
        // sqrt-family exponent.
        const sellSub = sqrtBuySubtotal(m, from, targetMinted - from);
        const sellNet = sellSub * (1 - effectiveFeeRate);
        const delta = costBasis > 0 ? (sellNet - costBasis) / costBasis : 0;
        return { ...t, sellNet, delta, targetMinted };
      });
    }

    // Legacy linear-curve — fill-based scenarios (25 / 60 / 100 % of cap).
    if (supplyCap <= 0) return null;
    const currentFill = nMinted / supplyCap;
    const allTiers = [
      { key: 'stall',    label: '25% filled',  fill: 0.25 },
      { key: 'moderate', label: '60% filled',  fill: 0.60 },
      { key: 'full',     label: '100% filled', fill: 1.00 },
    ];
    const activeTiers = mode === 'sell'
      ? allTiers.filter((t) => t.fill > currentFill + 0.01)
      : allTiers;
    if (activeTiers.length === 0) return null;
    return activeTiers.map((t) => {
      const targetMinted = Math.max(q, Math.floor(supplyCap * t.fill));
      const sellSub = (m * q * (2 * targetMinted - q + 1)) / 2;
      const sellNet = sellSub * (1 - effectiveFeeRate);
      const delta = costBasis > 0 ? (sellNet - costBasis) / costBasis : 0;
      return { ...t, sellNet, delta, targetMinted };
    });
  }, [creator, qty, mode, total, effectiveFeeRate]);

  // Curve-aware caps — sqrt clans have unlimited supply, so `soldOut` MUST
  // stay false regardless of bits_minted. Legacy `linear` clans keep the
  // old cap-based logic (`bits_minted >= supply_cap`). We keep MAX_BUY_QTY
  // as the client-side quantity ceiling for both, purely to bound the UI
  // stepper and the client-side quote math — it never gates soldOut.
  const isSqrt = isSqrtClan(creator?.clan);
  const supplyCap = creator?.clan?.supply_cap ?? MAX_BUY_QTY;
  const bitsMinted = creator?.clan?.bits_minted ?? 0;
  // Live whale cap — for sqrt clans this is the DYNAMIC cap the backend
  // actually enforces at trade time: max(100, bits_minted × whale_pct). We
  // read the server-computed value from `max_bits_per_user_current` so the
  // Max button + "You own X/Y" line match backend truth. Fallback to the
  // static field for older API responses.
  const maxPerUser = creator?.clan?.max_bits_per_user_current
    ?? creator?.clan?.max_bits_per_user
    ?? MAX_BUY_QTY;
  const minToJoin = creator?.clan?.min_bits_to_join ?? 0;
  const soldOut = !isSqrt && (!!creator?.clan?.sold_out || bitsMinted >= supplyCap);

  // Phase 9.6 — Pre-launch state (Clan minted but Bits locked until launch_at)
  const launchAtIso = creator?.clan?.launch_at;
  const isPrelaunch = !!(launchAtIso && new Date(launchAtIso) > new Date());
  const [reminded, setReminded] = useState(false);
  const [preCountdown, setPreCountdown] = useState('');
  useEffect(() => {
    if (!isPrelaunch) return;
    const tick = () => {
      const ms = new Date(launchAtIso) - new Date();
      if (ms <= 0) { setPreCountdown('Opening now…'); return; }
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      setPreCountdown(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isPrelaunch, launchAtIso]);
  const toggleRemind = async () => {
    if (!creator?.handle) return;
    try {
      if (reminded) {
        await api.delete(`/clans/${creator.handle}/remind`);
        setReminded(false);
      } else {
        await api.post(`/clans/${creator.handle}/remind`);
        setReminded(true);
      }
    } catch { /* silent */ }
  };
  const supplyRemaining = Math.max(0, supplyCap - bitsMinted);
  const roomForBuyer = Math.max(0, maxPerUser - holding);

  // Balance-limited max qty (closed-form quadratic for the linear bonding curve).
  //   subtotal = m × [q × n + q(q+1)/2]
  //   total    = subtotal × (1 + feeRate)
  //   Solve total ≤ balance for max integer q.
  //   Two branches: legacy linear clans have a clean quadratic; sqrt-family
  //   clans use the inverted revenue integral to solve for q directly.
  const balanceLimitedMax = (() => {
    if (mode !== 'buy' || !creator || balance == null || balance <= 0) return 0;
    const m = creator.clan.multiplier;
    const n = bitsMinted;
    const F = 1 + effectiveFeeRate;
    if (m <= 0 || F <= 0) return 0;
    if (isSqrtClan(creator.clan)) {
      // Sqrt-family: revenue(N) = (m/(1+exp)) × N^(1+exp)
      // Solve  revenue(n+q) − revenue(n) ≤ balance/F  →
      //   q_max = ((n^(1+exp) + balance × (1+exp) / (m × F)))^(1/(1+exp)) − n
      const CE = 0.667;         // must match /app/frontend/src/lib/curve.js
      const exp1 = 1 + CE;
      const rhs = Math.pow(n, exp1) + (balance * exp1) / (m * F);
      const qFloat = Math.pow(rhs, 1 / exp1) - n;
      return Math.max(0, Math.floor(qFloat));
    }
    // Legacy linear closed form: q² + (2n+1)q - 2B/(mF) ≤ 0
    const b = 2 * n + 1;
    const c = (2 * balance) / (m * F);
    const disc = b * b + 4 * c;
    if (disc < 0) return 0;
    const q = Math.floor((-b + Math.sqrt(disc)) / 2);
    return Math.max(0, q);
  })();

  const buyMaxQty = mode === 'buy'
    ? Math.min(supplyRemaining, roomForBuyer, balanceLimitedMax, MAX_BUY_QTY)
    : MAX_BUY_QTY;
  const maxQty = mode === 'sell' ? Math.max(1, holding || 1) : Math.max(1, buyMaxQty);
  const buyExceedsCap = mode === 'buy' && qty > buyMaxQty;

  // Auto-clamp buy qty whenever the affordable-max drops below the current
  // qty. Kills the "phantom insufficient-balance banner" bug where a
  // successful Max-buy drains the wallet, `setQty(3)` runs post-success as
  // the new default, but the freshly-updated balance can no longer afford
  // even 1 Bit → an alarming red banner appears for a trade the user never
  // asked for. When `buyMaxQty` is 0 we snap qty to 0 (not 1) so the
  // `total > balance` check evaluates false and the banner stays hidden.
  // Buy button is naturally disabled when qty < 1. Also covers price-moved-
  // mid-session and multi-tab drain cases.
  useEffect(() => {
    if (mode !== 'buy' || submitting) return;
    if (buyMaxQty <= 0) {
      if (qty !== 0) setQty(0);
      return;
    }
    if (qty > buyMaxQty) setQty(buyMaxQty);
  }, [mode, buyMaxQty, qty, submitting]);

  const confirmDisabled =
    submitting || !creator ||
    (mode === 'buy' && (soldOut || insufficientBalance || qty < 1 || buyExceedsCap)) ||
    (mode === 'sell' && (notEnoughBits || oversold || holding < 1 || qty < 1));

  const onConfirm = async () => {
    if (confirmDisabled || !creator) return;
    // Fire tactile audio tick immediately on user gesture (Buy = 720Hz, Sell = 520Hz).
    playTick(mode);
    setSubmitting(true);
    setFlash(null);
    setCommitInfo(null);
    // Phase 2B — compute slippage bound from the current quote. `Auto` sends
    // no ceiling (engine will accept whatever the freshly-computed total is);
    // tighter tiers send a max_total (buy) / min_total (sell) that scales
    // off the client-side quote total shown to the user.
    const opt = SLIPPAGE_OPTS.find((o) => o.key === slippage) || SLIPPAGE_OPTS[0];
    const slipPct = opt.pct;
    // Capture cost-basis snapshot BEFORE the trade so we can compute realized P&L on sell.
    let costBasisSnapshot = 0;
    let holdingSnapshot = holding;
    if (mode === 'sell') {
      try {
        const { data: hd } = await api.get(`/holdings/${creator.handle}`);
        costBasisSnapshot = Number(hd.cost_basis || 0);
        holdingSnapshot = Number(hd.bits_owned || 0);
      } catch {}
    }
    // Pure recomputation of the trade quote — mirrors the useMemo block
    // above but callable inside the retry loop with a *fresh* creator
    // snapshot + a possibly-shrunk qty. Only used to keep `max_total` /
    // `min_total` honest across retries when slippage is enabled. Delegates
    // to the shared `computeTradeQuote` helper so display and retry paths
    // share one source of truth for sqrt-vs-linear math.
    const quoteTotal = (creatorSnap, q, m2) => {
      if (!creatorSnap) return 0;
      return computeTradeQuote(creatorSnap.clan, q, m2, effectiveFeeRate).total;
    };
    try {
      const url = mode === 'buy' ? '/trade/buy' : '/trade/sell';
      // Phase 1 — Auto-retry up to 4 total attempts on recoverable errors.
      //
      // Two failure classes both surface during hyped batch launches:
      //   • 409 "Price moved / Batch was busy / past your ceiling" — the
      //     price advanced concurrently. Refresh the quote and retry with
      //     the SAME qty (fan can still afford it at the fresh price).
      //   • 400 "Insufficient balance / Not enough Points" (buy only) — the
      //     price jumped past the fan's balance. Refresh AND halve qty so
      //     a partial buy still lands instead of bouncing them entirely.
      //
      // Slippage ceilings (`max_total` / `min_total`) are recomputed each
      // attempt from the fresh creator snapshot + current qty — otherwise
      // a tight ceiling from the first quote would kill every retry.
      const originalQty = qty;
      let currentQty = qty;
      let currentCreator = creator;
      let attempt = 0;
      const MAX_ATTEMPTS = 4;
      let data = null;
      let lastErr = null;
      // Idempotency key — one per user tap, reused across retries.
      // Server dedups on (user_id, client_trade_id) so a retry that races
      // with an in-flight original never causes a double execution.
      const clientTradeId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `ct-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      while (attempt < MAX_ATTEMPTS) {
        const bodyExtras = {};
        if (slipPct != null) {
          const freshTotal = quoteTotal(currentCreator, currentQty, mode);
          if (mode === 'buy') bodyExtras.max_total = Number((freshTotal * (1 + slipPct)).toFixed(4));
          else bodyExtras.min_total = Number((freshTotal * (1 - slipPct)).toFixed(4));
        }
        try {
          const resp = await api.post(url, {
            handle: creator.handle,
            qty: currentQty,
            client_trade_id: clientTradeId,
            ...bodyExtras,
          });
          data = resp.data;
          break;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const detail = String(err?.response?.data?.detail || '').toLowerCase();
          // No HTTP response at all = network drop / proxy timeout / abort.
          // Poll /trade/status/{cid} to see if the trade actually landed
          // server-side before failing. Prevents the "Bits reduced but shown
          // as failed" bug users hit on Iris (Feb 2026).
          if (!err.response) {
            for (let poll = 0; poll < 3; poll += 1) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, 500));
              try {
                // eslint-disable-next-line no-await-in-loop
                const { data: statusData } = await api.get(`/trade/status/${clientTradeId}`);
                if (statusData && statusData.found && statusData.result) {
                  data = statusData.result;
                  break;
                }
              } catch (_) { /* keep polling */ }
            }
            if (data) break;
          }
          // 409 → keep qty, refresh price
          const is409Retry = status === 409 && (
            detail.includes('price moved') ||
            detail.includes('batch was busy') ||
            detail.includes('past your ceiling') ||
            detail.includes('dipped below your floor')
          );
          // 400 → halve qty (buy only — sell can't hit balance issues)
          const is400Retry = status === 400 && mode === 'buy' && (
            detail.includes('insufficient balance') ||
            detail.includes('not enough points')
          );
          if (!is409Retry && !is400Retry) throw err;
          if (attempt >= MAX_ATTEMPTS - 1) throw err;
          // Refresh creator snapshot (both branches benefit from fresh price)
          try {
            const { data: fresh } = await api.get(`/creators/${creator.handle}`);
            currentCreator = fresh;
            setCreator(fresh);
            setTickAge(0);
            setTickPulse((v) => v + 1);
          } catch { /* keep going; retry will re-error if truly bad */ }
          if (is400Retry) {
            const nextQty = Math.max(1, Math.floor(currentQty / 2));
            if (nextQty === currentQty) throw err;  // already at 1 — can't shrink further
            currentQty = nextQty;
            setQty(nextQty);
            setFlash({ kind: 'success', msg: `Price moved — retrying with ${nextQty} Bit${nextQty === 1 ? '' : 's'} (attempt ${attempt + 2}/${MAX_ATTEMPTS})` });
          } else {
            setFlash({ kind: 'success', msg: `Refreshing price… (attempt ${attempt + 2}/${MAX_ATTEMPTS})` });
          }
          attempt += 1;
          // Small linear backoff so we don't hammer the API in a hot loop
          await new Promise((r) => setTimeout(r, 250 * attempt));
        }
      }
      if (!data) throw lastErr || new Error('Trade failed after retries.');
      setBalance(data.balance_after);
      // Phase 2B — capture batch transparency for the chip. Only surfaces when
      // the trade was actually part of a batch (batch_size > 1). Single-trade
      // fast-paths omit these fields, so the chip stays hidden.
      if (data.batch_size && data.batch_size > 1) {
        setCommitInfo({
          batch_size: data.batch_size,
          batch_position: data.batch_position,
        });
      }
      const shrunk = currentQty < originalQty;
      setToast({
        msg: mode === 'buy'
          ? (shrunk
              ? `✓ Bought ${currentQty} Bit${currentQty > 1 ? 's' : ''} of @${creator.handle} for ${data.total.toFixed(2)} pts (scaled down from ${originalQty} — price moved during batch)`
              : `✓ Bought ${currentQty} Bit${currentQty > 1 ? 's' : ''} of @${creator.handle} for ${data.total.toFixed(2)} pts`)
          : `✓ Sold ${currentQty} Bit${currentQty > 1 ? 's' : ''} of @${creator.handle} — received ${data.total.toFixed(2)} pts`,
        tone: 'success',
      });
      setFlash({ kind: 'success', msg: `Balance now ${data.balance_after.toFixed(2)} pts · next price ${data.price_after.toFixed(2)} pts` });

      // Fire the celebratory share modal on every sell (profit OR loss)
      if (mode === 'sell') {
        const avgCost = holdingSnapshot > 0 ? costBasisSnapshot / holdingSnapshot : 0;
        const investedForSold = avgCost * currentQty;
        setSellShare({
          qty: currentQty,
          proceeds: Number(data.total || 0),
          invested: investedForSold,
        });
      }

      await reload();
      // Snap qty back so the next quote makes sense
      setQty(mode === 'sell' ? 1 : 3);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      setToast({ msg: detail || 'Trade failed.', tone: 'error' });
      setFlash({ kind: 'error', msg: detail });
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <>
        <BlobField />
        <div className="stage">
          <div className="card" style={{ textAlign: 'center' }}>
            <h1 className="heading">😕 {error}</h1>
            <button className="btn-primary" onClick={close} data-testid="trade-error-back">Back to Home</button>
          </div>
        </div>
      </>
    );
  }

  if (!creator || balance === null) {
    return (
      <>
        <BlobField />
        <div className="loading-shell" data-testid="trade-loading">loading trade…</div>
      </>
    );
  }

  return (
    <>
      <BlobField />
      <div className="scrim" onClick={close} data-testid="trade-scrim" />
      <div
        className="trade-stage"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div className="trade-sheet" data-testid="trading-sheet">
          <div className="drag-handle" />

          {/* Header */}
          <div className="sheet-header">
            <div
              className="sh-avatar"
              style={{ background: `linear-gradient(135deg, ${creator.gradient[0]}, ${creator.gradient[1]})` }}
              aria-hidden="true"
            >
              {creator.initials}
            </div>
            <div>
              <div className="sh-name">{creator.display_name}</div>
              <div className="sh-handle">@{creator.handle}</div>
            </div>
            <div className="sh-spacer" />
            <button
              type="button"
              className="ts-fair-badge"
              onClick={() => setShowFairness(true)}
              data-testid="ts-fair-badge"
              title={t('trade.fairByDesignTitle')}
            >
              <span aria-hidden="true">🎲</span>
              <span className="ts-fair-badge-text">{t('trade.fairByDesign')}</span>
            </button>
            <button className="close-btn" aria-label="Close" onClick={close} data-testid="trade-close-btn">
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Wallet strip */}
          <div className="wallet-strip" data-testid="wallet-strip">
            <div>
              <div className="ws-label">{t('trade.yourBalance')}</div>
              <div className="ws-value" data-testid="wallet-balance">{formatPts(balance, 2)} {t('common.pts')}</div>
            </div>
            <div className="ws-divider" />
            <div>
              <div className="ws-label">{t('trade.youOwn')}</div>
              <div className="ws-value" data-testid="wallet-holdings">{holding} {holding !== 1 ? t('common.bits') : t('common.bit')}</div>
            </div>
          </div>

          {/* Phase 4 P1 — Email verification gate. Shown INSTEAD of the trade
              form when the current user hasn't verified their email yet. */}
          {user && user.email_verified === false ? (
            <VerifyEmailGate context="trade" />
          ) : (
          <>

          {/* Phase 9.6 — Pre-launch state: replace whole trade form */}
          {isPrelaunch ? (
            <div className="ts-prelaunch" data-testid="ts-prelaunch">
              <div className="ts-prelaunch-emoji">🔜</div>
              <div className="ts-prelaunch-label">Launches in</div>
              <div className="ts-prelaunch-countdown" data-testid="ts-prelaunch-countdown">{preCountdown}</div>
              <div className="ts-prelaunch-when">
                {new Date(launchAtIso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
              <button
                type="button"
                className={`ts-remind-btn${reminded ? ' reminded' : ''}`}
                onClick={toggleRemind}
                data-testid="ts-remind-btn"
              >
                {reminded ? '✓ Reminder set' : '🔔 Set reminder'}
              </button>
            </div>
          ) : (
          <>
          {/* Buy/Sell toggle */}
          <div className="toggle-row" data-testid="trade-toggle">
            <button
              className={`toggle-btn${mode === 'buy' ? ' active' : ''}`}
              data-mode="buy"
              onClick={() => { setMode('buy'); setQty(3); setFlash(null); }}
              data-testid="toggle-buy"
            >{t('trade.buy')}</button>
            <button
              className={`toggle-btn${mode === 'sell' ? ' active' : ''}`}
              data-mode="sell"
              onClick={() => { setMode('sell'); setQty(Math.min(2, Math.max(1, holding))); setFlash(null); }}
              disabled={holding < 1}
              title={holding < 1 ? 'Buy some Bits before selling' : ''}
              data-testid="toggle-sell"
            >{t('trade.sell')}</button>
          </div>

          {/* Price row */}
          <div className="price-row">
            <div className="price-block">
              <div className="label">
                {t('trade.currentPrice')}
                <span
                  className="ts-live-dot"
                  data-testid="ts-live-dot"
                  title={tickAge < 3 ? t('trade.live') : `Updated ${tickAge}s ago`}
                >●</span>
                <span className="ts-live-age" data-testid="ts-live-age">
                  {tickAge < 3 ? t('trade.live') : `${tickAge}s`}
                </span>
              </div>
              <div
                className="value"
                key={tickPulse}
                data-testid="trade-current-price"
              >{creator.clan.current_price.toFixed(2)} {t('common.pts')}</div>
            </div>
            <div className="price-arrow" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div className="price-block next">
              <div className="label">{mode === 'buy' ? t('trade.priceAfterBuy') : t('trade.priceAfterSell')}</div>
              <div className="value" style={{ color: mode === 'buy' ? 'var(--success)' : 'var(--error)' }} data-testid="trade-next-price">
                {nextPrice.toFixed(2)} {t('common.pts')}
              </div>
            </div>
          </div>

          {/* Quantity stepper — free-form input + −/+ + scaled presets */}
          <div className="qty-card">
            <div className="qty-label">{mode === 'buy' ? t('trade.bitsToBuy') : t('trade.bitsToSell')}</div>
            <div className="qty-stepper">
              <button
                className="qty-btn"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={qty <= 1}
                data-testid="qty-minus"
                aria-label="Decrease quantity"
              >−</button>
              <input
                type="number"
                inputMode="numeric"
                className="qty-value qty-input"
                min={1}
                max={maxQty}
                step={1}
                value={qty}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') { setQty(''); return; }
                  const n = parseInt(raw, 10);
                  if (!Number.isFinite(n)) return;
                  setQty(Math.min(maxQty, Math.max(1, n)));
                }}
                onBlur={() => {
                  if (qty === '' || qty < 1) setQty(1);
                }}
                data-testid="qty-value"
                aria-label="Bits quantity"
              />
              <button
                className="qty-btn"
                onClick={() => setQty((q) => Math.min(maxQty, (Number(q) || 0) + 1))}
                disabled={qty >= maxQty}
                data-testid="qty-plus"
                aria-label="Increase quantity"
              >+</button>
            </div>
            <div className="qty-quick">
              {[1, 10, 100, 1000].map((n) => (
                <button
                  key={n}
                  className="qq-btn"
                  onClick={() => setQty(Math.min(maxQty, n))}
                  disabled={n > maxQty && n !== 1}
                  data-testid={`qty-quick-${n}`}
                >{n.toLocaleString()}</button>
              ))}
              {mode === 'sell' && holding > 0 && (
                <button className="qq-btn" onClick={() => setQty(holding)} data-testid="qty-max">
                  Max ({holding.toLocaleString()})
                </button>
              )}
              {mode === 'buy' && buyMaxQty > 0 && !soldOut && (
                <button
                  className="qq-btn"
                  onClick={() => setQty(buyMaxQty)}
                  data-testid="qty-max-buy"
                  title="Max you can buy given your balance and clan caps"
                >
                  Max ({buyMaxQty.toLocaleString()})
                </button>
              )}
            </div>
          </div>

          {/* Phase 9.4 — Min-to-join hint for buyers who haven't hit the threshold.
              Also hosts the compact "Visit Clan" button (right-aligned) — enabled
              once holding >= minToJoin, greyed out otherwise with a tooltip
              explaining how many more Bits are needed to unlock. Clicking it
              navigates to the Clan Feed and closes this trading sheet. */}
          {mode === 'buy' && minToJoin > 0 && !soldOut && (
            <div className="min-to-join-hint" data-testid="min-to-join-hint">
              <span className="min-to-join-emoji">🚪</span>
              <span className="min-to-join-msg">
                {holding >= minToJoin ? (
                  <>You're already in @{creator.handle}'s Clan — you own <b>{holding}</b> of {minToJoin} needed.</>
                ) : (
                  <>Need <b>{minToJoin}</b> Bit{minToJoin === 1 ? '' : 's'} to enter this Clan Feed. You own <b>{holding}</b>.</>
                )}
              </span>
              <button
                type="button"
                className={`min-to-join-cta${holding >= minToJoin ? '' : ' is-locked'}`}
                onClick={holding >= minToJoin ? () => navigate(`/clan/${creator.handle}/feed`, { state: { from: '/trade' } }) : undefined}
                disabled={holding < minToJoin}
                title={holding >= minToJoin
                  ? `Open @${creator.handle}'s Clan Feed`
                  : `Buy ${minToJoin - holding} more Bit${(minToJoin - holding) === 1 ? '' : 's'} to unlock`}
                aria-label={holding >= minToJoin
                  ? `Visit @${creator.handle}'s Clan Feed`
                  : `Visit Clan locked — buy ${minToJoin - holding} more Bits to unlock`}
                data-testid="min-to-join-visit-cta"
              >
                {holding >= minToJoin ? 'Visit Clan →' : '🔒 Visit Clan'}
              </button>
            </div>
          )}

          {/* Breakdown */}
          <div className="breakdown">
            <div className="b-row">
              <span>{qty} Bit{qty > 1 ? 's' : ''} · avg {avg.toFixed(2)} pts</span>
              <span className="val">{subtotal.toFixed(2)} pts</span>
            </div>
            <div className="b-row">
              <span>Fees ({(effectiveFeeRate * 100).toFixed(0)}%)</span>
              <span className="val">{mode === 'sell' ? '−' : ''}{fee.toFixed(2)} pts</span>
            </div>
            <div className="b-row total">
              <span>{mode === 'buy' ? 'You pay' : 'You receive'}</span>
              <span className="val" data-testid="trade-total">{total.toFixed(2)} pts</span>
            </div>
          </div>
          <p className="fee-note">
            Fees split: 5% to {creator.display_name} · 5% to Clanster
            {hasReferrer ? ' · 1% to your referrer' : ''}
          </p>

          {/* "What could happen?" — fill-scenario projections */}
          {scenarios && (
            <div className="scenarios-panel" data-testid="trade-scenarios-panel">
              <button
                type="button"
                className="scenarios-toggle"
                onClick={() => setShowScenarios((v) => !v)}
                data-testid="trade-scenarios-toggle"
                aria-expanded={showScenarios}
              >
                <span>
                  {mode === 'buy'
                    ? `📈 What your ${qty} Bit${qty > 1 ? 's' : ''} could become`
                    : `📈 What you could earn if you hold on`}
                </span>
                <span className={`scenarios-caret${showScenarios ? ' open' : ''}`}>▾</span>
              </button>
              {showScenarios && (
                <div className="scenarios-body" data-testid="trade-scenarios-body">
                  <div className="scenarios-sub">
                    {mode === 'buy'
                      ? `As more fans join @${creator.handle}, your Bits grow more valuable. Snapshot at different fill levels:`
                      : `Every new fan pushes the price up. If you wait for the Clan to grow, your ${qty} Bit${qty > 1 ? 's' : ''} could fetch:`}
                  </div>
                  <div className="scenarios-rows">
                    {scenarios.map((s) => {
                      const isLoss = s.delta < 0;
                      let multStr;
                      if (Math.abs(s.delta) < 1) {
                        multStr = `${isLoss ? '' : '+'}${(s.delta * 100).toFixed(0)}%`;
                      } else if (s.delta >= 10) {
                        multStr = `>10× ${mode === 'buy' ? 'entry' : 'current'}`;
                      } else {
                        multStr = `~${(1 + s.delta).toFixed(1)}× ${mode === 'buy' ? 'entry' : 'current'}`;
                      }
                      return (
                        <div
                          key={s.key}
                          className={`scenarios-row${isLoss ? ' loss' : ''}`}
                          data-testid={`trade-scenario-${s.key}`}
                        >
                          <span className="scenarios-tier">{s.label}</span>
                          <span className="scenarios-value">
                            <b>{s.sellNet.toFixed(2)} pts</b>
                            <span className="scenarios-delta">{multStr}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="scenarios-disclaimer">
                    Not a promise. Bits can lose value if fans sell out. Only spend what you can afford to lose.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Warning lines */}
          {mode === 'buy' && insufficientBalance && qty <= buyMaxQty && (
            <div className="trade-warn" data-testid="warn-insufficient">
              Insufficient balance — you need {formatPts(total, 2)} pts (have {formatPts(balance, 2)}).
            </div>
          )}
          {mode === 'sell' && notEnoughBits && (
            <div className="trade-warn" data-testid="warn-notenoughbits">
              You only own {holding} Bit{holding !== 1 ? 's' : ''} of @{creator.handle}.
            </div>
          )}

          {/* Phase 8 — cap gauge + sold-out banner. sqrt clans have no
              cap → skip the whole block (per-user whale cap still enforced
              server-side; the wizard-level 3% is silent). */}
          {mode === 'buy' && creator && !isSqrt && (
            <>
              {soldOut ? (
                <div className="trade-flash error" data-testid="trade-sold-out">
                  🔒 <b>Sold out</b> — this Clan is at capacity. Wait for a member to sell to join.
                </div>
              ) : (
                <div className="supply-progress-label" data-testid="trade-cap-gauge" style={{ marginBottom: 8 }}>
                  You own <b>{holding.toLocaleString()}/{maxPerUser.toLocaleString()}</b> Bits ·{' '}
                  <b>{supplyRemaining.toLocaleString()}</b> of {supplyCap.toLocaleString()} left
                  <div className="supply-progress">
                    <div className="supply-progress-fill" style={{ width: `${Math.min(100, (bitsMinted / supplyCap) * 100)}%` }} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Phase 2B — Slippage tolerance chips (hidden on sold-out/waitlist path) */}
          {!(mode === 'buy' && soldOut) && !submitting && (
            <div className="ts-slip-row" data-testid="ts-slip-row">
              <div className="ts-slip-label">
                Max slippage
                <span
                  className="ts-slip-help"
                  title="If the price moves past your ceiling before the batch runs, your trade is skipped instead of overpaying."
                  aria-label="Slippage tolerance help"
                >?</span>
              </div>
              <div className="ts-slip-chips">
                {SLIPPAGE_OPTS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`ts-slip-chip${slippage === o.key ? ' active' : ''}`}
                    onClick={() => setSlippagePersist(o.key)}
                    data-testid={`ts-slip-${o.key}`}
                  >{o.label}</button>
                ))}
              </div>
              <div className="ts-slip-hint" data-testid="ts-slip-hint">
                {slippage === 'auto'
                  ? 'Auto ✓ — always fills your trade at a fair price. Recommended.'
                  : 'Tighter tiers can bounce your trade if the price moves during the batch shuffle. Only useful on hyped launches — Auto is safest.'}
              </div>
            </div>
          )}

          {/* Confirm — or Join Waitlist on sold-out clans, or Commit view during submit */}
          {mode === 'buy' && soldOut ? (
            <button
              className="btn-confirm buy"
              disabled={submitting}
              onClick={async () => {
                if (submitting) return;
                setSubmitting(true);
                try {
                  const desiredQty = Math.max(1, Math.min(qty || 1, maxPerUser - holding));
                  const { data } = await api.post(`/clans/${creator.handle}/waitlist/join`, { qty: desiredQty });
                  navigate('/waitlist');
                } catch (ex) {
                  const msg = ex?.response?.data?.detail || 'Could not join waitlist.';
                  alert(typeof msg === 'string' ? msg : 'Could not join waitlist.');
                } finally {
                  setSubmitting(false);
                }
              }}
              data-testid="waitlist-join-btn"
            >
              {submitting ? 'Joining…' : `🎟️ Join Waitlist for ${Math.max(1, qty || 1)} Bit${(qty || 1) > 1 ? 's' : ''}`}
            </button>
          ) : submitting ? (
            /* Phase 2B — Commit & Settle locked strip. Replaces the button while
               the trade is being settled by the batch engine. */
            <div className="ts-commit" data-testid="ts-commit-strip">
              <div className="ts-commit-lock" aria-hidden="true">🔒</div>
              <div className="ts-commit-copy">
                <div className="ts-commit-head">
                  Locked in ·&nbsp;
                  <b>{mode === 'buy' ? 'Buy' : 'Sell'} {qty} Bit{qty > 1 ? 's' : ''}</b>
                </div>
                <div className="ts-commit-sub" data-testid="ts-commit-countdown">
                  {commitMs > 0
                    ? `Batch fires in ${(commitMs / 1000).toFixed(1)}s · random shuffle keeps it fair`
                    : 'Settling…'}
                </div>
                {queueSnap && queueSnap.queued >= 3 && (
                  <div className="ts-commit-queue" data-testid="ts-commit-queue">
                    🔥 <b>{queueSnap.queued}</b> other{queueSnap.queued === 1 ? '' : 's'} locked in with you ·&nbsp;
                    est <b>{queueSnap.est_wait_s < 1
                      ? `${Math.round(queueSnap.est_wait_s * 1000)}ms`
                      : `${queueSnap.est_wait_s.toFixed(1)}s`}</b> wait · shuffled fair
                  </div>
                )}
                <div className="ts-commit-bar">
                  <div
                    className="ts-commit-bar-fill"
                    style={{ width: `${((500 - commitMs) / 500) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (() => {
            // Drain state — the confirm button has 3 flavours when
            // qty=0 and buyMaxQty=0 on the buy tab:
            //   (a) balance too low → "Add balance to buy more →" (deep-links
            //       to /wallet?openDeposit=1 which auto-opens the Deposit sheet)
            //   (b) at personal whale cap but balance is fine → informative
            //       "Whale cap reached — wait for a sell" (no nav, disabled)
            //   (c) fallback → same as (a)
            const isDrain = mode === 'buy' && qty === 0 && buyMaxQty === 0;
            const drainByBalance = isDrain && balanceLimitedMax === 0;
            const drainByWhaleCap = isDrain && !drainByBalance && roomForBuyer === 0;
            const drainLabel = drainByWhaleCap
              ? '🐋 Whale cap reached — wait for a sell'
              : 'Add balance to buy more →';
            const drainOnClick = drainByBalance || (isDrain && !drainByWhaleCap)
              ? () => navigate('/wallet?openDeposit=1')
              : undefined;
            return (
            <button
              className={`btn-confirm ${mode}`}
              disabled={isDrain ? drainByWhaleCap : confirmDisabled}
              onClick={isDrain ? drainOnClick : onConfirm}
              data-testid="trade-confirm-btn"
            >
              {mode === 'buy'
                ? (isDrain
                    ? drainLabel
                    : t('trade.buyCta', { qty, unit: qty > 1 ? t('common.bits') : t('common.bit'), total: total.toFixed(2) }))
                : t('trade.sellCta', { qty, unit: qty > 1 ? t('common.bits') : t('common.bit'), total: total.toFixed(2) })}
            </button>
            );
          })()}

          {/* Phase 2B — Batch transparency chip. Only renders if the settled
              trade was actually part of a batch (batch_size > 1). */}
          {commitInfo && (
            <div className="ts-batch-chip" data-testid="ts-batch-chip">
              <span aria-hidden="true">🎲</span>
              &nbsp;Batch of <b>{commitInfo.batch_size}</b> · your trade landed at&nbsp;
              <b>#{commitInfo.batch_position}</b> (randomised)
            </div>
          )}

          {flash && (
            <div className={`trade-flash ${flash.kind}`} data-testid={`trade-flash-${flash.kind}`}>
              {flash.msg}
            </div>
          )}
          </>
          )}
          </>
          )}

          <p className="signup-note ts-sheet-footer" style={{ marginTop: 14 }}>
            <Link to="/home" data-testid="trade-back-link">{t('common.backToHome')}</Link>
            <Link
              to={`/trade?clan=${handle}`}
              className="ts-full-trade-link"
              data-testid="ts-open-full-trade"
              onClick={close}
            >{t('trade.openFullTradeView')}</Link>
          </p>
        </div>
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      {sellShare && (
        <SellShareModal
          open={!!sellShare}
          onClose={() => setSellShare(null)}
          creatorName={creator.display_name}
          creatorHandle={creator.handle}
          qty={sellShare.qty}
          proceeds={sellShare.proceeds}
          invested={sellShare.invested}
          referralLink={`${window.location.origin}/onboarding${user?.referral_code ? `?code=${user.referral_code}` : ''}`}
        />
      )}
      {showFairness && (
        <>
          <div
            className="ssm-scrim"
            onClick={() => setShowFairness(false)}
            data-testid="ts-fair-scrim"
          />
          <div
            className="ssm-modal ts-fair-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ts-fair-title"
            data-testid="ts-fair-modal"
          >
            <button
              className="ssm-close"
              onClick={() => setShowFairness(false)}
              aria-label="Close"
              data-testid="ts-fair-close"
            >
              <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <div className="ts-fair-hero" aria-hidden="true">🎲</div>
            <h2 id="ts-fair-title" className="ts-fair-title">Fair by design</h2>
            <p className="ts-fair-lede">
              Here's how we make sure every trade — whether you're on the
              couch or a data center — gets the exact same shot.
            </p>
            <ul className="ts-fair-list">
              <li>
                <b>Microsecond receipts.</b> Every trade is stamped with the
                exact microsecond it arrived. You can see them in the Activity
                tab of any Clan.
              </li>
              <li>
                <b>500ms batch windows.</b> When a Clan gets hot, we group all
                trades that land in the same half-second and run them
                together — one batch at a time.
              </li>
              <li>
                <b>Random order inside each batch.</b> Within a batch, the
                order is <em>shuffled</em> before execution. Being 3ms faster
                doesn't put you in front of anyone.
              </li>
              <li>
                <b>Honest errors.</b> If your batch position lost out, we
                tell you clearly — no cryptic codes, no guessing why.
              </li>
              <li>
                <b>Quiet Clans stay instant.</b> If nobody else is trading,
                your Bit goes through immediately. Batching only kicks in
                when it's needed.
              </li>
            </ul>
            <div className="ts-fair-replay" data-testid="ts-fair-replay">
              <div className="ts-fair-replay-title">Want to verify it yourself?</div>
              <p className="ts-fair-replay-lede">
                Download a signed JSON of every trade on <b>@{handle}</b> —
                arrival timestamps, batch shuffles, prices. Ed25519 signed by
                Clanster; anyone can verify it hasn't been touched.
              </p>
              <button
                type="button"
                className="ts-fair-replay-btn"
                data-testid="ts-fair-replay-btn"
                onClick={() => setReplayModalOpen(true)}
              >
                🔍 View signed replay
              </button>
            </div>
            <button
              type="button"
              className="ts-fair-cta"
              onClick={() => setShowFairness(false)}
              data-testid="ts-fair-cta"
            >
              Got it
            </button>
          </div>
        </>
      )}
      <ReplayModal
        open={replayModalOpen}
        handle={handle}
        onClose={() => setReplayModalOpen(false)}
      />
    </>
  );
}
